// =========================================================================
// admin-sms: platform-admin + org-owner SMS support flows.
//
// Wraps the `admin-sms` edge function (POST /functions/v1/admin-sms),
// which re-enforces auth (platform_admin or org admin) inside the RPCs
// it calls.
// =========================================================================

import { supabase } from '../client';
import { unwrapFunctionsInvokeError } from '../internal/functionsInvokeError';

interface AdminSmsOk {
  ok: true;
  challenge_id?: string;
  expires_at?: string;
  verified?: boolean;
  purpose?: string | null;
  message_id?: string;
  phone?: string;
  verified_at?: string | null;
}

interface AdminSmsError {
  ok: false;
  error: string;
}

type AdminSmsResponse = AdminSmsOk | AdminSmsError;

interface AdminSmsRequest {
  action:
    | 'send_user_verification'
    | 'verify_user_otp'
    | 'set_user_phone'
    | 'reset_mfa'
    | 'send_invitation_sms';
  user_id?: string;
  code?: string;
  reason?: string;
  invitation_id?: string;
  phone?: string;
  token?: string;
}

async function callAdminSms(body: AdminSmsRequest): Promise<AdminSmsOk> {
  const { data, error } = await supabase().functions.invoke<AdminSmsResponse>('admin-sms', {
    body: body as unknown as Record<string, unknown>,
  });
  if (error) {
    throw new Error(await unwrapFunctionsInvokeError(error, 'admin-sms call failed'));
  }
  if (!data) throw new Error('admin-sms returned no body');
  if (!data.ok) throw new Error(data.error);
  return data;
}

/**
 * Send a verification OTP to a user. The OTP code itself never leaves the
 * DB; this is the SUPPORT flow where the user reads the code back to the
 * operator over the phone, and the operator then submits it via
 * `verifyUserOtpAsAdmin`. The user can ALSO confirm it themselves via the
 * normal phone-confirm RPC if they're at a screen.
 */
export async function sendUserVerificationSms(userId: string): Promise<{
  challenge_id: string;
  expires_at: string;
}> {
  const r = await callAdminSms({ action: 'send_user_verification', user_id: userId });
  return { challenge_id: r.challenge_id!, expires_at: r.expires_at! };
}

/**
 * Platform admin seeds (or replaces) a user's phone on file. The number is
 * stored UNVERIFIED -- the operator must then run `sendUserVerificationSms`
 * + `verifyUserOtpAsAdmin` (or have the user verify it themselves) before
 * the row is treated as the user's authoritative 2FA channel.
 *
 * Phone must be E.164 (e.g. `+15551234567`); the server enforces this and
 * raises a clear error if the format is wrong, so we don't pre-validate
 * client-side beyond a trim.
 */
export async function setUserPhone(
  userId: string,
  phone: string,
): Promise<{ phone: string; verified_at: string | null }> {
  const r = await callAdminSms({
    action: 'set_user_phone',
    user_id: userId,
    phone: phone.trim(),
  });
  return { phone: r.phone!, verified_at: r.verified_at ?? null };
}

/** Operator enters the code the user just read them over the phone. */
export async function verifyUserOtpAsAdmin(
  userId: string,
  code: string,
): Promise<{ verified: boolean; purpose: string | null }> {
  const r = await callAdminSms({ action: 'verify_user_otp', user_id: userId, code });
  return { verified: Boolean(r.verified), purpose: r.purpose ?? null };
}

/**
 * Emergency MFA reset: disables 2FA on a user's account WITHOUT requiring
 * an OTP. Used when a user has lost both their phone AND their recovery
 * codes. Fires a `security_event_alert` SMS to the user's old number
 * (best-effort -- if their phone is offline, the SMS just queues).
 */
export async function resetUserMfa(userId: string, reason?: string): Promise<void> {
  await callAdminSms({ action: 'reset_mfa', user_id: userId, reason });
}

/**
 * Sends an invitation SMS for an EXISTING invitation row. Authorized for
 * platform admins always; for org admins of the invitation's org as well.
 *
 * `token` is the plaintext invite token the caller just received back
 * from `rpc_org_invite_member` (or `rpc_org_resend_invitation` after a
 * rotate). The DB only persists the sha256 hash in
 * `core.user_invitations.token_sha256`, so the server cannot reconstruct
 * the plaintext — `core.rpc_send_invitation_sms` sha256-validates the
 * caller-supplied token against the stored hash before queuing the text.
 *
 * The recipient must have already opted in to the platform SMS line
 * (texted us first). If they haven't, the underlying Sendblue queue
 * will refuse and this throws with a clear error.
 */
export async function sendInvitationSms(args: {
  invitationId: string;
  phone: string;
  token: string;
}): Promise<{ message_id: string }> {
  const r = await callAdminSms({
    action: 'send_invitation_sms',
    invitation_id: args.invitationId,
    phone: args.phone,
    token: args.token,
  });
  return { message_id: r.message_id! };
}
